import { Link } from "react-router-dom";
import { Copy, ExternalLink, Images } from "lucide-react";
import type { Comment } from "@/lib/api";
import { classLabel, displayAuthorName, painLabel, platformLabel } from "@/lib/commentLabels";
import { Button } from "@/components/ui/button";
import { cn, relativeTime } from "@/lib/utils";

function classStyle(storyClass: string): string {
  if (storyClass === "historia_identyfikacja") return "border-brand/30 bg-brand/10 text-brand";
  if (storyClass === "pytanie") return "border-amber-200 bg-amber-50 text-amber-800";
  if (storyClass === "cta_only") return "border-emerald-200 bg-emerald-50 text-emerald-800";
  if (storyClass === "atak_forma") return "border-red-200 bg-red-50 text-red-800";
  return "border-slate-200 bg-slate-50 text-slate-600";
}

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* ignore */
  }
}

type Props = {
  items: Comment[];
  isLoading: boolean;
  total: number;
};

export default function CommentsList({ items, isLoading, total }: Props) {
  if (isLoading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        Ładowanie komentarzy…
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        Brak komentarzy w wybranym filtrze.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        Pokazuję {items.length} z {total}
      </div>
      {items.map((c) => {
        const pain = c.pain_ids[0];
        const quote = `"${c.text.slice(0, 280)}"\n- komentarz ${platformLabel(c.platform)} / ${c.video_title}`;
        return (
          <article
            key={c.id || `${c.platform}-${c.post_id}-${c.author}`}
            className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-lg font-semibold text-slate-900">
                {displayAuthorName(c.author, c.platform_id || c.platform)}
              </h3>
              {c.created_at && (
                <time className="text-sm text-slate-500">{relativeTime(c.created_at)}</time>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                {platformLabel(c.platform)}
              </span>
              {(c.class_label || c.story_class) && (
                <span
                  className={cn(
                    "inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium",
                    classStyle(c.story_class),
                  )}
                >
                  {c.class_label || classLabel(c.story_class)}
                </span>
              )}
              {c.action && (
                <span className="inline-flex rounded-full border border-brand/30 bg-brand/10 px-2.5 py-0.5 text-xs font-medium text-brand">
                  do akcji
                </span>
              )}
              {c.cta && (
                <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                  CTA Mama
                </span>
              )}
              {c.is_reply && (
                <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-500">
                  odpowiedź
                </span>
              )}
            </div>
            {c.video_title && (
              <div className="mt-2 text-xs text-slate-500">
                {c.post_url ? (
                  <a
                    href={c.post_url}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-slate-800"
                  >
                    {c.video_title}
                  </a>
                ) : (
                  c.video_title
                )}
              </div>
            )}
            <p className="mt-3 text-base leading-relaxed text-slate-700 whitespace-pre-wrap">
              {c.text}
            </p>
            {(c.pain_ids.length > 0 || c.like_count > 0) && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {c.pain_ids.map((id) => (
                  <span
                    key={id}
                    className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600"
                  >
                    {painLabel(id)}
                  </span>
                ))}
                {c.like_count > 0 && (
                  <span className="self-center text-xs text-slate-400">{c.like_count} like</span>
                )}
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
              <Button size="sm" variant="outline" onClick={() => copy(c.text)}>
                <Copy className="mr-1.5 h-3.5 w-3.5" />
                Kopiuj
              </Button>
              <Button size="sm" variant="ghost" onClick={() => copy(quote)}>
                Cytat do karuzeli
              </Button>
              {c.post_url ? (
                <Button size="sm" variant="ghost" asChild>
                  <a href={c.post_url} target="_blank" rel="noreferrer">
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                    Rolka
                  </a>
                </Button>
              ) : null}
              {pain ? (
                <Link
                  to={`/karuzele?pain=${encodeURIComponent(String(pain))}`}
                  className="inline-flex h-8 items-center rounded-md px-3 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
                >
                  <Images className="mr-1.5 h-3.5 w-3.5" />
                  {painLabel(pain)} w karuzelach
                </Link>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}
